/**
 * Unsaved-changes detection for the checklist item form.
 *
 * ⚠️ THE PROBLEM THIS SOLVES IS SILENT DATA LOSS. An admin turns on Example
 * photos, uploads four, writes a caption on each, then closes the panel — and
 * every bit of it is gone, with no warning and no sign anything was lost. Only
 * "Update Item" commits. The examples in particular are expensive to redo:
 * four uploads and four sentences.
 *
 * ⚠️ A FINGERPRINT, NOT A BOOLEAN SET BY EVERY HANDLER. The form has around
 * twenty controls across two near-duplicate copies, so a `setDirty(true)` in
 * each is forty call sites, and the one that gets missed is the one that loses
 * work. Comparing a snapshot taken on open against one taken on close cannot
 * miss a field, and it correctly reports NOT dirty when someone types a
 * character and takes it back out again.
 */

import type { ExampleDraft } from './exampleDraft';

/** The item fields a person can actually change in the form. */
const TRACKED_FIELDS = [
  'item_name',
  'description',
  'item_type',
  'item_category',
  'is_required',
  'group_id',
  'template_id',
  'allow_attachments',
  'examples_enabled',
  'item_values',
  'validation_rules',
  'permissions',
  'user_permissions',
  'conditional_logic',
] as const;

export interface ItemFormState {
  item: Record<string, unknown> | null | undefined;
  draft: ExampleDraft | null | undefined;
}

/**
 * A stable string for the form's current contents.
 *
 * ⚠️ `null` and `undefined` and `''` all normalise together. The form writes
 * `description: null` where the row held `undefined`, and treating that as an
 * edit would warn about unsaved changes on a form nobody touched — which
 * trains people to click Discard without reading.
 */
export function itemFormFingerprint({ item, draft }: ItemFormState): string {
  const normalised: Record<string, unknown> = {};

  for (const field of TRACKED_FIELDS) {
    normalised[field] = normalise(item?.[field]);
  }

  // Examples: what they are, in what order, with what captions and verdicts.
  normalised.examples = (draft?.entries ?? []).map((e) => [
    e.storagePath,
    e.caption.trim(),
    e.verdict,
  ]);

  // A removal is a change even when the remaining list looks untouched, which
  // it does when the last example is deleted and the rest were already empty.
  normalised.removed = [...(draft?.removedPaths ?? [])].sort();

  return JSON.stringify(normalised);
}

function normalise(value: unknown): unknown {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  if (Array.isArray(value) && value.length === 0) return null;
  return value;
}

/**
 * Whether the form has changed since the baseline was taken.
 *
 * ⚠️ A NULL BASELINE IS NOT DIRTY. The baseline is set once the form and its
 * examples have finished loading; before that, everything would compare
 * unequal and closing a form nobody had touched would raise the warning.
 */
export function itemFormIsDirty(baseline: string | null, current: string): boolean {
  if (baseline === null) return false;
  return baseline !== current;
}
